import * as css from './suspendedConfirmation.css';
import {useSearchParams} from '../../hooks/useSearchParams';
import {useEffect, useState} from 'react';
import {chargeSuspendedOrder, suspendedConfirmation} from '../../redux/event/eventActions';
import {useLoaderData} from 'react-router-dom';
import PaymentConfirmation from './components/willComPaymentApprove';
import WillComPayment from './components/willComPayment';
import WillComeSuccess from './components/willComeSuccess';
import TickchakLoader from '../../component/tickchakLoader/TickchakLoader';
import ChargeFailed from './components/chargeFailed';
import Failed from './components/failed';
import {TICK_DOMAIN} from '../../config/config';
import WontComeSuccess from './components/wontComeSuccess';
import MaybeSuccess from './components/maybeSuccess';
import WontComeMessage from './components/wontComeMessage';
import AlreadyChargedMessage from './components/alreadyChargedMessage';
import Canceled from './components/canceled';
import {get} from 'lodash';
import DecisionScreen from './components/decisionScreen';
import {useTranslation} from 'react-i18next';
import UserMessage from '../../layout/userMessages/UserMessage';

export interface OrderData {
    // Required fields based on usage
    totalPrice: string;
    oid: string;
    nameFirst: string;
    nameLast: any;
    lang: string;
    eventTitle: string;
    contactName: string;
    contactPhone: string;

    // Optional fields based on component usage
    ticketUrl?: string;
    location?: string;
    timeStart?: string;
    timeEnd?: string;
    confirmUrl?: string;
    maybeUrl?: string;
    cancelUrl?: string;
    eventDomain?: string;
    eventName?: string;
    ticketsAmount?: number;
    ticketTitle?: string;
    creditCard?: string;
    email?: string;
    phone?: string;
    emailContent?: string;
    ticketsQr?: Array<{
        id: string;
        qid: string;
        price: number;
        title: string;
        description?: string;
        type?: string;
        date?: string;
        x?: number;
        y?: number;
        limitByTickets: number;
        tid: string;
    }>;
}

const steps = {
    start: 'start',
    failed: 'failed',
    error404: '404',
    willCome: {
        alreadyRejected: 'alreadyRejected',
        alreadyCharged: 'alreadyCharged',
        payment: {
            approve: 'approve',
            charge: {
                start: 'chargeStart',
                success:'chargeSuccess',
            },
            chargeFailed: 'chargeFailed',
            success: 'success',
        },
        free: {
            success: 'success',
        },
        canceled: 'canceled',
    },
    wontCome: {
        success: 'wontCome'
    },
    maybe: {
        success: 'maybe',
    },
    decisionScreen: 'decisionScreen'
};

function getStatusValue(statusString: string): string | null {
    if(!statusString) return null;
    const path = statusString.split('_');
    const result = get(steps, path, steps.error404);
    return typeof result === 'string' ? result : steps.error404;
}

function SuspendedConfirmation() {
    const {confirm} = useSearchParams() as {confirm?: string};
    const redirect = () => window.location.replace(TICK_DOMAIN);
    const {i18n} = useTranslation('suspendedConfirmation');
    // Get loader data - all confirmation data is now pre-loaded
    const loaderData = useLoaderData() as {
        success: boolean;
        confirm?: string;
        status?: string;
        order?: OrderData;
        language?: string;
        preloadedData?: {
            hasConfirmation: boolean;
            hasOrder: boolean;
            hasLanguage: boolean;
        };
        shouldRedirect?: boolean;
        error?: string;
    } | undefined;
    
    // Initialize state directly from loader data (no useEffect needed!)
    const [orderData, setOrderData] = useState(loaderData?.order || {} as OrderData);
    const [currentStep, setCurrentStep] = useState(
        loaderData?.success && loaderData?.status 
            ? getStatusValue(loaderData.status) 
            : loaderData?.success === false 
                ? steps.failed 
                : getStatusValue(steps.start)
    );
    
    // Handle language change (minimal useEffect)
    const approvePayment = async () => {
        if(confirm) {
            const res = await chargeSuspendedOrder(confirm);
            if(res.status) setCurrentStep(getStatusValue(res.status));
            if(res.order) setOrderData(res.order as OrderData);

        }
    };

    const handleDecisionResponse = (res: any) => {
        if(!res || !res.status) {
            setCurrentStep(steps.failed);
            return;
        }
        setCurrentStep(getStatusValue(res.status));
        if(res.order) setOrderData(res.order as OrderData);
    };

    useEffect(() => {
        i18n.changeLanguage(orderData.lang).then();
    }, [orderData.lang]);

    const componentMap = {
        start: () => <TickchakLoader showClass={'show'} cssStyle={undefined} />,
        failed: () => <Failed />,
        approve: () => <PaymentConfirmation approvePayment={approvePayment} orderData={orderData} />,
        chargeStart: () => <WillComPayment loading={true} onFinishClicked={() => setCurrentStep(steps.willCome.payment.success)} orderData={orderData} />,
        chargeSuccess: () => <WillComPayment loading={false} onFinishClicked={() => setCurrentStep(steps.willCome.payment.success)} orderData={orderData} />,
        chargeFailed: () => <ChargeFailed orderData={orderData} />,
        success: () => <WillComeSuccess orderData={orderData} />,
        canceled: () => <Canceled orderData={orderData} />,
        wontCome: () => <WontComeSuccess orderData={orderData}/>,
        maybe: () => <MaybeSuccess orderData={orderData} />,
        alreadyRejected: () => <WontComeMessage orderData={orderData} />,
        alreadyCharged: () => <AlreadyChargedMessage orderData={orderData} />,
        decisionScreen: () => <DecisionScreen orderData={orderData} onResponse={handleDecisionResponse} />,
        '404': () => {
            redirect();
            return <></>;
        }
    };

    return (
        <css.container role="main" aria-label="Order confirmation">
            <UserMessage/>

            {(componentMap[currentStep] || componentMap['404'])()}

        </css.container>
    );
}

export default SuspendedConfirmation;
